// Constants
import { LoggingStrings } from "@ms/uno-constants/lib/local/LoggingConstants";
import { LoopHostAppName } from "@ms/uno-constants/lib/local/LoopConstants";
import { createNewOneDSSink } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
import { MsaTenantId } from "@ms/uno-constants/lib/local/AppConstants";
import { getUserAliasFromUpn } from "@ms/uno-utilities/lib/local/StringUtilities";
import { convertTenantFeedbackSettingsToFeedbackPolicy, getLoopPolicyPayload } from "./TenantFeedbackSettingsUtilities";
/**
 * Get the loop package dependency synthesizer props.
 * This generates minimal props required for synthesizing a minimal set of dependencies which are sufficient for working
 * with Loop Component APIs for CRUD operations.
 * @param configProvider Config provider
 * @param locale Locale
 * @param userContext User context
 * @param loggers Loggers
 * @param tokenProvider Token provider
 * @param plannerTelemetryContext Telemetry context
 * @param loopScenarioLifecycle Lifecycle of the scenario in which Loop is being used
 * @param loopScenarioName Name of the scenario in which Loop is being used
 */ export function getLoopPackageDependencySynthesizerProps(configProvider, locale, userContext, loggers, tokenProvider, plannerTelemetryContext, loopScenarioLifecycle, loopScenarioName) {
    const { sessionMetaData, settings } = configProvider();
    const internationalizationContextProps = {
        uiLocale: locale
    };
    const telemetryContext = {
        hostSessionId: sessionMetaData.sessionId,
        hostCorrelationId: sessionMetaData.appLoadCorrelationId,
        hostName: LoggingStrings.ProductName,
        hostAppPlatform: "Web",
        hostAppName: LoopHostAppName,
        hostAppVersion: sessionMetaData.clientBuild,
        hostScenarioLifecycle: loopScenarioLifecycle,
        hostScenarioName: loopScenarioName
    };
    const privacyGuardContext = {
        userName: userContext.displayName,
        userAlias: telemetryContext.userContext?.user?.upn ? getUserAliasFromUpn(telemetryContext.userContext?.user?.upn) : undefined
    };
    const telemetrySink = createNewOneDSSink(plannerTelemetryContext, configProvider, privacyGuardContext, LoggingStrings.ProductName);
    if (!telemetrySink) {
        throw new Error("Unable to initialize telemetry sink.");
    }
    return {
        internationalizationContextProps,
        loggers,
        rolloutStage: settings.rolloutStage,
        telemetryContext,
        tokenProviderProps: {
            defaultResource: settings.serviceConfigurations.graph.authResourceURI,
            getToken: tokenProvider
        },
        userContextProps: userContext,
        telemetrySink
    };
}
/**
 * Get the loop dependency synthesizer props.
 * This generates an extensive set of props which are necessary and sufficient for generating an extensive
 * set of dependencies along with the dependency synthesizer, required for rendering the Loop UX.
 * @param configProvider Config provider
 * @param locale Locale
 * @param userContext User context
 * @param loggers Loggers
 * @param tokenProvider Token provider
 * @param plannerTelemetryContext Telemetry context
 * @param documentInfo Parts of the resolved URL of the Fluid document
 * @param v8Theme Fluent V8 theme
 * @param v9Theme Fluent V9 theme
 * @param feedbackPolicy Feedback policy defined for the user
 * @param getPolicyPayload Callback to get policy payload from a settingId
 * @param loopScenarioLifecycle Lifecycle of the scenario in which Loop is being used
 * @param loopScenarioName Name of the scenario in which Loop is being used
 */ export function getLoopDependenciesProps(configProvider, locale, userContext, loggers, tokenProvider, plannerTelemetryContext, documentInfo, v8Theme, v9Theme, feedbackPolicy, getPolicyPayload, loopScenarioLifecycle, loopScenarioName) {
    const { sessionMetaData } = configProvider();
    const { environment } = sessionMetaData;
    const loopPackageDependencySynthesizerProps = getLoopPackageDependencySynthesizerProps(configProvider, locale, userContext, loggers, tokenProvider, plannerTelemetryContext, loopScenarioLifecycle, loopScenarioName);
    return {
        ...loopPackageDependencySynthesizerProps,
        documentInfo,
        environment,
        feedbackPolicy,
        v8Theme,
        v9Theme,
        getPolicyPayload
    };
}
/**
 * Generated the telemetry context based on the provided context.
 * @param context - The IContext object
 * @returns The telemetry context object.
 */ function getTelemetryContext(context) {
    return {
        locale: context.locale,
        userId: context.userId,
        tenantId: context.tenantId,
        ringId: context.ringId,
        isMSA: context.tenantId === MsaTenantId,
        isGuestUser: context.isGuestUser,
        platform: context.platform
    };
}
/**
 * Constructs the function to generate the loop dependencies props
 * @param appContext
 * @param tenantFeedbackSettings
 * @param configProvider
 * @param fetchAccessTokenCallback
 * @param loggers
 * @param loopTelemetryScenario
 * @returns The function to generate the loop dependencies props
 */ export function getLoopDependenciesPropsGenerator(appContext, tenantFeedbackSettings, configProvider, fetchAccessTokenCallback, loggers, loopTelemetryScenario) {
    const generateLoopDependenciesProps = (documentInfo, v8Theme, v9Theme)=>{
        const userContextProps = {
            userId: appContext.userId ?? "",
            tenantId: appContext.tenantId ?? "",
            isGuest: appContext.isGuestUser ?? false,
            displayName: appContext.displayName ?? ""
        };
        const telemetryContext = getTelemetryContext(appContext);
        const feedbackSettings = tenantFeedbackSettings;
        const feedbackPolicy = convertTenantFeedbackSettingsToFeedbackPolicy(feedbackSettings);
        const getPolicyPayload = (policyId)=>getLoopPolicyPayload(policyId, feedbackSettings);
        const fetchAccessToken = fetchAccessTokenCallback;
        return getLoopDependenciesProps(configProvider, appContext.locale, userContextProps, loggers, fetchAccessToken, telemetryContext, documentInfo, v8Theme, v9Theme, feedbackPolicy, getPolicyPayload, "Load", loopTelemetryScenario);
    };
    return generateLoopDependenciesProps;
}
